import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { SortDirection } from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import { LogoBoxed, Modal, NoData } from "components";
import { accountLogoFallbackImage } from "const";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import {
  getQuantumDemandDomainContacts,
  getQuantumDemandDomainMeta,
  listPaginatedQuantumDemand,
} from "../../QuantumDemand.requests";
import { QuantumDemandContactCard } from "../QuantumDemandContactCard";
import { QuantumIndexGauge } from "../QuantumIndexGauge";
import { useBuyingGroupProfileParam } from "./BuyingGroupProfile.hooks";

const INITIAL_VISIBLE_CONTACTS = 6;
const VISIBLE_CONTACTS_INCREMENT = 3;

interface BuyingGroupProfileModalProps {
  campaignId: number;
  domain: string | null;
}

export const BuyingGroupProfileModal = ({
  campaignId,
  domain,
}: BuyingGroupProfileModalProps) => {
  const { closeBuyingGroupProfile } = useBuyingGroupProfileParam();

  const { data, isFetching: isFetchingDomainRow } = useQuery(
    ["quantumDemandDomainRow", campaignId, domain],
    async () => {
      const { results } = await listPaginatedQuantumDemand(campaignId, {
        page: 1,
        per_page: 1,
        order_by: "quantumIndex",
        order: SortDirection.DESC,
        domains: [String(domain)],
      });

      return results[0] ?? null;
    },
    { enabled: Boolean(domain) },
  );

  const { data: domainMeta, isFetching: isFetchingDomainMeta } = useQuery(
    ["quantumDemandDomainMeta", campaignId, domain],
    () => getQuantumDemandDomainMeta(campaignId, String(domain)),
    { enabled: Boolean(domain) },
  );

  const { data: contacts = [], isFetching: isFetchingContacts } = useQuery(
    ["quantumDemandDomainContacts", campaignId, domain],
    () => getQuantumDemandDomainContacts(campaignId, String(domain)),
    { enabled: Boolean(domain) },
  );

  const [visibleContactsCount, setVisibleContactsCount] = useState(
    INITIAL_VISIBLE_CONTACTS,
  );

  useEffect(() => {
    setVisibleContactsCount(INITIAL_VISIBLE_CONTACTS);
  }, [campaignId, domain]);

  const firmographics = [
    domainMeta?.industry,
    domainMeta?.employeeRange && `${domainMeta.employeeRange} employees`,
    domainMeta?.revenueRange && `${domainMeta.revenueRange} revenue`,
  ].filter(Boolean);

  const renderBody = () => {
    if (isFetchingContacts) {
      return <Skeleton h="240px" borderRadius="lg" />;
    }

    if (contacts.length > 0) {
      return (
        <Box>
          <Heading fontSize="lg" mb={4}>
            Buying Group Engagement
          </Heading>
          <Flex flexWrap="wrap" justifyContent="center" gap={4}>
            {contacts.slice(0, visibleContactsCount).map((contact) => (
              <QuantumDemandContactCard key={contact.email} contact={contact} />
            ))}
          </Flex>
          {visibleContactsCount < contacts.length && (
            <Flex justifyContent="center" mt={4}>
              <Button
                size="sm"
                onClick={() =>
                  setVisibleContactsCount(
                    (count) => count + VISIBLE_CONTACTS_INCREMENT,
                  )
                }
              >
                Show more
              </Button>
            </Flex>
          )}
        </Box>
      );
    }

    return (
      <NoData
        height="240px"
        message="Buying group profile data is not available yet."
      />
    );
  };

  return (
    <Modal
      size="viewContainer"
      isOpen={Boolean(domain)}
      onClose={closeBuyingGroupProfile}
      title={
        <Box>
          <Flex
            position="relative"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
          >
            <Flex alignItems="center" gap={4}>
              <Box borderRadius={4}>
                <Image
                  minW="35px"
                  boxSize="35px"
                  objectFit="contain"
                  src={data?.logoUrl}
                  fallbackSrc={accountLogoFallbackImage}
                />
              </Box>
              <Flex direction="column" gap={1}>
                <Skeleton isLoaded={!isFetchingDomainMeta}>
                  <Text>{domainMeta?.companyName ?? domain}</Text>
                </Skeleton>
                <Skeleton isLoaded={!isFetchingDomainMeta}>
                  <Text fontSize={12} color={"gray.400"} maxWidth={330}>
                    {firmographics.join(" • ")}
                  </Text>
                </Skeleton>
                <Skeleton isLoaded={!isFetchingDomainMeta}>
                  <Text fontSize={12}>
                    Intent Model: {domainMeta?.campaignName}
                  </Text>
                </Skeleton>
                <Skeleton isLoaded={!isFetchingDomainMeta}>
                  <Text fontSize={12} color={"teal.500"}>
                    {domainMeta?.buyerResearchStage
                      ? `Buyer Research Stage: ${domainMeta?.buyerResearchStage}`
                      : ""}
                  </Text>
                </Skeleton>
              </Flex>
            </Flex>
            {data && (
              <Flex
                position="absolute"
                left="50%"
                top="50%"
                transform="translate(-50%, -50%)"
                alignItems="center"
                gap={3}
              >
                <Box boxSize="64px">
                  <QuantumIndexGauge value={data.quantumIndex} />
                </Box>
                <Text textTransform="uppercase" fontSize={14}>
                  quantum index
                </Text>
              </Flex>
            )}
            <Flex
              direction="column"
              justifyContent="space-between"
              w={180}
              gap={2}
            >
              <LogoBoxed adaptToDarkMode={true} />
              <Box
                border="1px"
                borderColor="teal.500"
                borderRadius="full"
                opacity={0.7}
                textAlign="center"
                py={2}
                px={4}
              >
                <Flex alignItems="center" gap={3}>
                  <Box
                    background="teal.500"
                    borderRadius="full"
                    w={1.5}
                    h={1.5}
                  ></Box>
                  <Skeleton isLoaded={!isFetchingDomainRow}>
                    <Text fontSize={12}>
                      Last updated:{" "}
                      {DateTime.now().startOf("week").toFormat("MMM dd")}
                    </Text>
                  </Skeleton>
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </Box>
      }
      body={renderBody()}
    />
  );
};
